import { useMemo, useState } from "react";
import {
  projectileYAtX,
  type TrigCannonConfig,
  type TrigCannonInstance,
  type TrigCannonSubmission,
} from "../../puzzles/core/trigCannon";
import { Button, Card, Pill } from "../ui";
import { Icon } from "../Icon";

export function TrigCannonPlayer({
  config,
  instance,
  onLock,
}: {
  config: TrigCannonConfig;
  instance: TrigCannonInstance;
  onLock(submission: TrigCannonSubmission): void;
}) {
  const [angle, setAngle] = useState(42);
  const [power, setPower] = useState(28);
  const [simulationProgress, setSimulationProgress] = useState<number | null>(
    null,
  );
  const trajectoryPoints = useMemo(() => {
    const result: Array<{ x: number; y: number }> = [];
    for (let x = 0; x <= Math.max(85, instance.target.x + 8); x += 1.5) {
      const y = projectileYAtX(x, angle, power, config.gravity);
      if (y < -3) break;
      result.push({ x: 50 + x * 6, y: 315 - y * 7 });
    }
    return result;
  }, [angle, config.gravity, instance.target.x, power]);
  const points = trajectoryPoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");
  const targetSvg = {
    x: 50 + instance.target.x * 6,
    y: 315 - instance.target.y * 7,
  };
  const angleRadians = (angle * Math.PI) / 180;
  const isSimulating = simulationProgress !== null;
  const projectilePoint =
    simulationProgress === null
      ? null
      : trajectoryPoints[
          Math.min(
            trajectoryPoints.length - 1,
            Math.round((trajectoryPoints.length - 1) * simulationProgress),
          )
        ];
  const lockSubmission = () => {
    if (isSimulating) return;
    runSimulation(setSimulationProgress, () => onLock({ angle, power }));
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[.65fr_1.35fr]">
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">
            Deflector controls
          </h3>
          <Pill tone="indigo">Trajectory</Pill>
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          Tune launch angle and velocity. The dotted gold prediction must cross
          the glitch core.
        </p>
        <div className="mt-6 space-y-6">
          <Control
            label="Launch angle"
            suffix="°"
            value={angle}
            min={config.angleMin}
            max={config.angleMax}
            disabled={isSimulating}
            onChange={setAngle}
          />
          <Control
            label="Launch speed"
            suffix=" m/s"
            value={power}
            min={config.powerMin}
            max={config.powerMax}
            disabled={isSimulating}
            onChange={setPower}
          />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Readout
            label="Horizontal"
            value={(power * Math.cos(angleRadians)).toFixed(1)}
          />
          <Readout
            label="Vertical"
            value={(power * Math.sin(angleRadians)).toFixed(1)}
          />
        </div>
        <Button
          className="mt-6 w-full"
          onClick={lockSubmission}
          disabled={isSimulating}
        >
          <Icon name="Crosshair" size={18} />
          {isSimulating ? "Simulating arc..." : "Lock firing solution"}
        </Button>
      </Card>

      <Card className="overflow-hidden p-3 sm:p-5">
        <div className="mb-3 flex items-center justify-between px-2">
          <span className="font-retro text-[8px] tracking-widest text-slate-400">
            TOWER DEFENSE MONITOR
          </span>
          <span className="text-[10px] font-bold text-amber-300">
            g = {config.gravity}m/s²
          </span>
        </div>
        <svg
          viewBox="0 0 600 360"
          className="h-auto w-full rounded-2xl border border-slate-800 bg-slate-950"
          role="img"
          aria-label={`Projectile trajectory at ${angle} degrees and ${power} meters per second toward target ${instance.target.x}, ${instance.target.y}.`}
        >
          <defs>
            <linearGradient id="tower-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#111d3b" />
              <stop offset="1" stopColor="#08111f" />
            </linearGradient>
            <filter id="target-glow">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <rect width="600" height="360" fill="url(#tower-sky)" />
          {[80, 160, 240, 320, 400, 480, 560].map((x) => (
            <line key={x} x1={x} y1="0" x2={x} y2="330" stroke="#1e293b" />
          ))}
          {[70, 140, 210, 280].map((y) => (
            <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="#1e293b" />
          ))}
          <line x1="0" y1="315" x2="600" y2="315" stroke="#4f46e5" strokeWidth="4" />
          <polyline
            points={points}
            fill="none"
            stroke="#fbbf24"
            strokeWidth="3"
            strokeDasharray="7 7"
          />
          <circle
            cx={targetSvg.x}
            cy={targetSvg.y}
            r={config.tolerance * 7}
            fill="rgba(244,63,94,.13)"
            stroke="#fb7185"
            strokeWidth="3"
            filter="url(#target-glow)"
          />
          <circle cx={targetSvg.x} cy={targetSvg.y} r="7" fill="#fb7185" />
          <text
            x={targetSvg.x}
            y={targetSvg.y - config.tolerance * 7 - 12}
            textAnchor="middle"
            fill="#fda4af"
            fontSize="11"
            fontWeight="800"
          >
            GLITCH CORE
          </text>
          <g transform={`translate(50 315) rotate(${-angle})`}>
            <rect x="-8" y="-9" width="60" height="18" rx="8" fill="#818cf8" />
            <circle r="17" fill="#334155" stroke="#a5b4fc" strokeWidth="3" />
          </g>
          {projectilePoint && (
            <circle
              cx={projectilePoint.x}
              cy={projectilePoint.y}
              r="8"
              fill="#fde68a"
              stroke="#f59e0b"
              strokeWidth="3"
            />
          )}
          <text x="26" y="34" fill="#94a3b8" fontSize="11">
            Target ({instance.target.x}m, {instance.target.y}m)
          </text>
        </svg>
      </Card>
    </div>
  );
}

function Control({
  label,
  suffix,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  suffix: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange(value: number): void;
}) {
  return (
    <label className="block">
      <span className="flex justify-between text-xs font-bold text-slate-300">
        {label}
        <span className="font-retro text-[9px] text-indigo-300">
          {value}
          {suffix}
        </span>
      </span>
      <input
        className="mt-3 w-full"
        type="range"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/65 p-3">
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-1 font-retro text-[9px] text-indigo-300">{value}</p>
    </div>
  );
}

function runSimulation(
  setProgress: (progress: number | null) => void,
  onComplete: () => void,
) {
  const duration = 1700;
  const startedAt = window.performance.now();

  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    setProgress(progress);
    if (progress < 1) {
      window.requestAnimationFrame(tick);
      return;
    }
    window.setTimeout(() => {
      setProgress(null);
      onComplete();
    }, 250);
  };

  setProgress(0);
  window.requestAnimationFrame(tick);
}
